/**
 * Style Parameter Logger
 *
 * Enable/disable logging by setting STYLE_LOGGER_ENABLED in the .env file:
 * - STYLE_LOGGER_ENABLED=true  -> Enable logging
 * - STYLE_LOGGER_ENABLED=false -> Disable logging
 * - Leave unset -> Log only in development mode (default)
 */
import { useStyles } from '@wix/tpa-settings/react';
import { useParamLogger } from './paramLogger';

interface StyleParamsObject {
  [key: string]: any;
}

/**
 * Custom hook for logging all style parameter changes in widget components
 *
 * Environment Control:
 * Add to your .env file:
 * ```
 * STYLE_LOGGER_ENABLED=true   # Enable logging
 * STYLE_LOGGER_ENABLED=false  # Disable logging
 * # Leave unset for development mode default
 * ```
 *
 * Usage:
 * ```typescript
 * import stylesParams from '../../stylesParams';
 * import { useStyleParamLogger } from '../utils/styleParamLogger';
 *
 * export const MyWidgetComponent = () => {
 *   useStyleParamLogger(stylesParams, 'MyWidget');
 *   // ... rest of component
 * };
 * ```
 *
 * @param stylesParams - The stylesParams object (e.g., from Login/stylesParams.ts)
 */
export const useStyleParamLogger = (stylesParams: StyleParamsObject) => {
  const styles = useStyles();

  useParamLogger(stylesParams, styles.get.bind(styles), {
    envVarName: 'STYLE_LOGGER_ENABLED',
    logEmoji: '🎨',
    paramTypeName: 'style',
  });
};
